'use strict'

// One repeat rule on a record. The calendar expands it while drawing.
// A booking view reads the same occurrences as slots. Nothing here writes a row per day.

const { calendarDayInTimeZone } = require('./app-timezone')
const { eventText } = require('./calendar-event')

const CALENDAR_LAYOUTS = ['month', 'week', 'day', 'list']
const CALENDAR_OCCURRENCE_CAP = 366
const FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly']

function pad (number) {

  return String(number).padStart(2, '0')
}

function formatDay (year, month, day) {

  return `${year}-${pad(month)}-${pad(day)}`
}

function dayParts (dayKey) {

  const match = String(dayKey || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)

  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const utc = new Date(Date.UTC(year, month - 1, day))

  if (utc.getUTCFullYear() !== year || utc.getUTCMonth() !== month - 1 || utc.getUTCDate() !== day) return null

  return { year, month, day, utc }
}

function weekdayOf (dayKey) {

  const parts = dayParts(dayKey)

  return parts ? parts.utc.getUTCDay() : null
}

function addDays (dayKey, amount) {

  const parts = dayParts(dayKey)

  if (!parts || !Number.isInteger(amount)) return null

  const next = new Date(parts.utc.getTime())

  next.setUTCDate(next.getUTCDate() + amount)

  return formatDay(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate())
}

function addMonths (dayKey, months) {

  const parts = dayParts(dayKey)

  if (!parts || !Number.isInteger(months)) return null

  const index = (parts.month - 1) + months
  const year = parts.year + Math.floor(index / 12)
  const month = ((index % 12) + 12) % 12
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()

  if (parts.day > last) return null

  return formatDay(year, month + 1, parts.day)
}

function normalizeCalendarLayout (value) {

  const name = String(value || '').trim().toLowerCase()

  return CALENDAR_LAYOUTS.indexOf(name) === -1 ? 'month' : name
}

function intervalOf (value) {

  const number = Number(value)

  if (!Number.isInteger(number) || number < 1) return 1

  return Math.min(number, 99)
}

function countOf (value) {

  if (value == null || value === '') return null

  const number = Number(value)

  if (!Number.isInteger(number) || number < 1) return null

  return Math.min(number, CALENDAR_OCCURRENCE_CAP)
}

function untilOf (value) {

  if (value == null || value === '') return ''

  const day = String(value).trim().slice(0, 10)

  return dayParts(day) ? day : ''
}

function weekdaysOf (value) {

  if (!Array.isArray(value)) return []

  const days = []

  for (const item of value) {

    const day = Number(item)

    if (!Number.isInteger(day) || day < 0 || day > 6) continue
    if (days.indexOf(day) === -1) days.push(day)
  }

  days.sort((left, right) => left - right)

  return days
}

function clipText (value) {

  if (value == null) return ''

  return String(value).trim().slice(0, 500)
}

function exceptionsOf (value) {

  if (!Array.isArray(value)) return []

  const exceptions = []
  const seen = new Set()

  for (const item of value) {

    if (!item || typeof item !== 'object') continue

    const date = untilOf(item.date)

    if (!date || seen.has(date)) continue

    const action = item.action === 'skip' ? 'skip' : (item.action === 'override' ? 'override' : '')

    if (!action) continue

    const exception = { date, action }

    if (action === 'override') {

      const occurrenceDate = untilOf(item.occurrenceDate)
      const title = clipText(item.title)
      const description = clipText(item.description)

      if (occurrenceDate) exception.occurrenceDate = occurrenceDate
      if (title) exception.title = title
      if (description) exception.description = description
    }

    seen.add(date)
    exceptions.push(exception)
  }

  return exceptions
}

function parseRecurrence (value) {

  if (value == null || value === '') return null

  let source = value

  if (typeof value === 'string') {

    const text = value.trim()

    if (!text) return null

    try {

      source = JSON.parse(text)
    } catch (error) {

      return null
    }
  }

  if (!source || typeof source !== 'object' || Array.isArray(source)) return null
  if (FREQUENCIES.indexOf(source.frequency) === -1) return null

  return {
    frequency: source.frequency,
    interval: intervalOf(source.interval),
    weekdays: weekdaysOf(source.weekdays),
    until: untilOf(source.until),
    count: countOf(source.count),
    exceptions: exceptionsOf(source.exceptions)
  }
}

function recurrenceText (rule) {

  const parsed = parseRecurrence(rule)

  return parsed ? JSON.stringify(parsed) : ''
}

function editSeries (rule, patch) {

  const current = parseRecurrence(rule) || {
    frequency: 'weekly',
    interval: 1,
    weekdays: [],
    until: '',
    count: null,
    exceptions: []
  }
  const nextPatch = patch && typeof patch === 'object' ? patch : {}
  const next = {
    frequency: FREQUENCIES.indexOf(nextPatch.frequency) === -1 ? current.frequency : nextPatch.frequency,
    interval: nextPatch.interval == null ? current.interval : nextPatch.interval,
    weekdays: nextPatch.weekdays == null ? current.weekdays : nextPatch.weekdays,
    until: Object.prototype.hasOwnProperty.call(nextPatch, 'until') ? nextPatch.until : current.until,
    count: Object.prototype.hasOwnProperty.call(nextPatch, 'count') ? nextPatch.count : current.count,
    exceptions: current.exceptions
  }

  return parseRecurrence(next)
}

function editOccurrence (rule, seriesDate, exception) {

  const current = parseRecurrence(rule)
  const date = untilOf(seriesDate)
  const change = exception && typeof exception === 'object' ? exception : {}
  const action = change.action === 'skip' ? 'skip' : (change.action === 'override' ? 'override' : '')

  if (!current || !date || !action) return null

  const next = {
    frequency: current.frequency,
    interval: current.interval,
    weekdays: current.weekdays,
    until: current.until,
    count: current.count,
    exceptions: current.exceptions.filter(item => item.date !== date)
  }

  next.exceptions.push({
    date,
    action,
    occurrenceDate: action === 'override' ? change.occurrenceDate : '',
    title: action === 'override' ? change.title : '',
    description: action === 'override' ? change.description : ''
  })

  return parseRecurrence(next)
}

function calendarRange (layout, dayKey) {

  const name = normalizeCalendarLayout(layout)
  const parts = dayParts(dayKey)

  if (!parts) return null

  const day = formatDay(parts.year, parts.month, parts.day)

  if (name === 'day') return { layout: name, start: day, end: day }

  if (name === 'week') {

    const start = addDays(day, -weekdayOf(day))

    return { layout: name, start, end: addDays(start, 6) }
  }

  const first = formatDay(parts.year, parts.month, 1)

  if (name === 'list') {

    const last = new Date(Date.UTC(parts.year, parts.month, 0)).getUTCDate()

    return { layout: name, start: first, end: formatDay(parts.year, parts.month, last) }
  }

  const start = addDays(first, -weekdayOf(first))

  return { layout: 'month', start, end: addDays(start, 41) }
}

function seriesDates (start, rule, rangeEnd) {

  const limit = rule.until && rule.until < rangeEnd ? rule.until : rangeEnd
  const cap = rule.count == null ? CALENDAR_OCCURRENCE_CAP : Math.min(rule.count, CALENDAR_OCCURRENCE_CAP)
  const dates = []

  if (rule.frequency === 'daily') {

    let day = start

    while (day && day <= limit && dates.length < cap) {

      dates.push(day)
      day = addDays(day, rule.interval)
    }

    return dates
  }

  if (rule.frequency === 'weekly') {

    const days = rule.weekdays.length ? rule.weekdays : [weekdayOf(start)]
    let weekStart = addDays(start, -weekdayOf(start))

    while (weekStart && weekStart <= limit && dates.length < cap) {

      let past = false

      for (const weekday of days) {

        const day = addDays(weekStart, weekday)

        if (!day || day < start) continue
        if (day > limit) {

          past = true
          break
        }

        dates.push(day)
        if (dates.length >= cap) return dates
      }

      if (past) break

      const nextWeek = addDays(weekStart, 7 * rule.interval)

      if (!nextWeek || nextWeek <= weekStart) break

      weekStart = nextWeek
    }

    return dates
  }

  let step = 0

  while (dates.length < cap && step < 2400) {

    const day = rule.frequency === 'yearly'
      ? addMonths(start, step * rule.interval * 12)
      : addMonths(start, step * rule.interval)

    step += 1

    if (!day) continue
    if (day > limit) break

    dates.push(day)
  }

  return dates
}

function expandRecurrence ({ anchor, rule, rangeStart, rangeEnd, timeZone } = {}) {

  const start = calendarDayInTimeZone(anchor, timeZone)

  if (!start) return []

  const windowStart = untilOf(rangeStart) || start
  const windowEnd = untilOf(rangeEnd) || start

  if (windowEnd < windowStart) return []

  const parsed = parseRecurrence(rule)

  if (!parsed) {

    if (start < windowStart || start > windowEnd) return []

    return [{ date: start, seriesDate: start, exception: false, title: null, description: null }]
  }

  const exceptions = new Map(parsed.exceptions.map(item => [item.date, item]))
  const occurrences = []

  for (const seriesDate of seriesDates(start, parsed, windowEnd)) {

    const exception = exceptions.get(seriesDate)

    if (exception && exception.action === 'skip') continue

    const date = exception && exception.occurrenceDate ? exception.occurrenceDate : seriesDate

    if (date < windowStart || date > windowEnd) continue

    occurrences.push({
      date,
      seriesDate,
      exception: !!exception,
      title: exception && exception.title ? exception.title : null,
      description: exception && exception.description ? exception.description : null
    })
  }

  return occurrences
}

function calendarFormDate (dayKey) {

  const day = untilOf(dayKey)

  return day ? `${day}T00:00` : ''
}

function calendarSlots (rows, view, options) {

  const slots = []
  const dateColumn = view && view.dateColumn
  const titleColumn = view && view.titleColumn
  const descriptionColumn = view && view.descriptionColumn
  const recurrenceColumn = view && view.recurrenceColumn

  for (const row of rows || []) {

    if (!row) continue

    const occurrences = expandRecurrence({
      anchor: dateColumn ? row[dateColumn] : null,
      rule: recurrenceColumn ? row[recurrenceColumn] : null,
      rangeStart: options && options.rangeStart,
      rangeEnd: options && options.rangeEnd,
      timeZone: options && options.timeZone
    })

    for (const occurrence of occurrences) {

      const title = occurrence.title || (titleColumn ? eventText(row[titleColumn]) : '')
      const description = occurrence.description || (descriptionColumn ? eventText(row[descriptionColumn]) : '')

      slots.push({
        recordId: row.id == null ? null : row.id,
        date: occurrence.date,
        seriesDate: occurrence.seriesDate,
        title,
        description,
        exception: occurrence.exception,
        start: occurrence.date,
        end: addDays(occurrence.date, 1)
      })
    }
  }

  slots.sort((left, right) => {

    if (left.date < right.date) return -1
    if (left.date > right.date) return 1
    if (String(left.recordId) < String(right.recordId)) return -1
    if (String(left.recordId) > String(right.recordId)) return 1

    return left.seriesDate < right.seriesDate ? -1 : (left.seriesDate > right.seriesDate ? 1 : 0)
  })

  return slots
}

module.exports = {
  CALENDAR_LAYOUTS,
  CALENDAR_OCCURRENCE_CAP,
  parseRecurrence,
  recurrenceText,
  editSeries,
  editOccurrence,
  expandRecurrence,
  calendarRange,
  calendarFormDate,
  calendarSlots,
  normalizeCalendarLayout,
  weekdayOf,
  addDays
}
